<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { useToastStore } from '../stores/toast'
import { useI18n } from 'vue-i18n'
import categoryService from '../services/category.service'
import coachService from '../services/coach.service'
import Multiselect from 'vue-multiselect'
import 'vue-multiselect/dist/vue-multiselect.css'

const router = useRouter()
const authStore = useAuthStore()
const toastStore = useToastStore()
const { t } = useI18n()

const categoryName = ref('')
const selectedCoaches = ref<any[]>([])
const availableCoaches = ref<any[]>([])
const loading = ref(false)
const loadingCoaches = ref(false)
const error = ref<string | null>(null)
const validationErrors = ref<Record<string, string[]>>({})

// Get club UUID from init data
const clubUuid = authStore.initData?.club?.uuid || null

// Fetch available coaches
const fetchCoaches = async () => {
  if (!clubUuid) {
    error.value = t('categories.errorNoClub')
    return
  }

  loadingCoaches.value = true

  try {
    console.log('Fetching coaches for club:', clubUuid)
    const response = await coachService.getCoaches(clubUuid, 9999)
    console.log('Coaches response:', response)
    availableCoaches.value = response.data || []
  } catch (err: any) {
    console.error('Error fetching coaches:', err)
    error.value = err.response?.data?.message || t('categories.errorFetchingCoaches')
  } finally {
    loadingCoaches.value = false
  }
}

// Handle form submission
const handleSubmit = async () => {
  if (!clubUuid) {
    error.value = t('categories.errorNoClub')
    return
  }

  loading.value = true
  error.value = null
  validationErrors.value = {}

  try {
    const data = {
      name: categoryName.value.trim(),
      coach_uuids: selectedCoaches.value.map(coach => coach.uuid)
    }

    const response = await categoryService.createCategory(clubUuid, data)

    // Show success toast with message from backend
    const message = response?.message || t('categories.categoryCreated')
    toastStore.success(message)

    // Redirect to categories list
    router.push('/categories')
  } catch (err: any) {
    console.error('Create category error:', err)

    // Handle validation errors
    if (err.response?.data?.errors) {
      validationErrors.value = err.response.data.errors
    } else {
      error.value = err.response?.data?.message || t('categories.errorCreatingCategory')
    }
  } finally {
    loading.value = false
  }
}

// Get validation error for field
const getFieldError = (fieldName: string): string | null => {
  const errors = validationErrors.value[fieldName]
  return errors && errors.length > 0 ? errors[0] : null
}

// Check if field has error
const hasFieldError = (fieldName: string): boolean => {
  return !!validationErrors.value[fieldName]
}

// Handle cancel
const handleCancel = () => {
  router.push('/categories')
}

// Get coach display name for multiselect
const customLabel = (coach: any) => {
  const firstName = coach.first_name || ''
  const lastName = coach.last_name || ''
  const fullName = `${firstName} ${lastName}`.trim()
  return fullName || coach.email || 'Bez nazwy'
}

onMounted(() => {
  fetchCoaches()
})
</script>

<template>
  <main class="bg-gray-50 min-h-screen">
    <div class="w-full px-4 py-4 md:py-4 py-2">
      <!-- Back Button -->
      <div class="mb-4">
        <router-link to="/categories" class="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 hover:underline">
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd"/>
          </svg>
          {{ t('categories.backToList') }}
        </router-link>
      </div>

      <!-- Page Header -->
      <div class="mb-4 md:mb-4 mb-2">
        <h1 class="text-2xl font-bold text-gray-900">{{ t('categories.addCategory') }}</h1>
      </div>

      <!-- Error Message -->
      <div v-if="error" class="mb-4 p-4 bg-red-50 border-2 border-red-300 text-red-700 rounded">
        {{ error }}
      </div>

      <!-- Form -->
      <div class="bg-white border-2 border-gray-300 rounded shadow-sm p-6">
        <form @submit.prevent="handleSubmit">
          <div class="grid grid-cols-1 gap-4">
            <!-- Category Name -->
            <div>
              <label for="categoryName" class="block text-sm font-medium text-gray-700 mb-1">
                {{ t('categories.categoryName') }} <span class="text-red-600">{{ t('common.required') }}</span>
              </label>
              <input
                v-model="categoryName"
                type="text"
                id="categoryName"
                required
                :class="[
                  'w-full px-3 py-2 text-sm border-2 rounded focus:outline-none',
                  hasFieldError('name')
                    ? 'border-red-500 focus:border-red-600'
                    : 'border-gray-300 focus:border-blue-600'
                ]"
              >
              <p v-if="getFieldError('name')" class="mt-1 text-xs text-red-600">
                {{ getFieldError('name') }}
              </p>
            </div>

            <!-- Coaches -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                {{ t('categories.coaches') }}
              </label>
              <Multiselect
                v-model="selectedCoaches"
                :options="availableCoaches"
                :multiple="true"
                :close-on-select="false"
                :clear-on-select="false"
                :preserve-search="true"
                :placeholder="t('categories.selectCoaches')"
                label="name"
                track-by="uuid"
                :custom-label="customLabel"
                :loading="loadingCoaches"
                :class="[
                  hasFieldError('coach_uuids') ? 'multiselect-error' : ''
                ]"
              >
                <template #noResult>
                  <span>{{ t('categories.noCoachesFound') }}</span>
                </template>
                <template #noOptions>
                  <span>{{ t('categories.noCoachesAvailable') }}</span>
                </template>
              </Multiselect>
              <p v-if="getFieldError('coach_uuids')" class="mt-1 text-xs text-red-600">
                {{ getFieldError('coach_uuids') }}
              </p>
            </div>
          </div>

          <!-- Submit Button -->
          <div class="flex justify-end gap-2 mt-6">
            <button
              type="button"
              @click="handleCancel"
              class="px-4 py-2 bg-gray-100 text-sm font-medium text-gray-700 rounded hover:bg-gray-200 border-2 border-gray-300"
            >
              {{ t('common.cancel') }}
            </button>
            <button
              type="submit"
              :disabled="loading"
              class="px-4 py-2 bg-green-600 text-sm font-medium text-white rounded hover:bg-green-700 border-2 border-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {{ loading ? t('categories.adding') : t('categories.addCategory') }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </main>
</template>

